/**
 * Gann & additional drawing primitives.
 *
 * Tools: Gann Box, Gann Fan, Gann Square, Regression Trend,
 *        Disjoint Channel, Flat Top/Bottom Channel
 */

const HANDLE_RADIUS = 5;
const HANDLE_COLOR = '#6366f1';
const HANDLE_HIT = 8;
const HIT_TOLERANCE = 7;

function drawHandle(ctx, x, y, hpr, vpr) {
  ctx.beginPath();
  ctx.arc(x * hpr, y * vpr, HANDLE_RADIUS * hpr, 0, Math.PI * 2);
  ctx.fillStyle = HANDLE_COLOR; ctx.fill();
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5 * hpr; ctx.stroke();
}

function hexToRgba(hex, a) {
  const c = hex.replace('#', '');
  return `rgba(${parseInt(c.slice(0,2),16)},${parseInt(c.slice(2,4),16)},${parseInt(c.slice(4,6),16)},${a})`;
}

function distToSeg(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1, len2 = dx*dx + dy*dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1);
  let t = Math.max(0, Math.min(1, ((px-x1)*dx + (py-y1)*dy) / len2));
  return Math.hypot(px - (x1 + t*dx), py - (y1 + t*dy));
}

class Base {
  constructor() { this._chart = null; this._series = null; this._requestUpdate = null; this.isHovered = false; this.isSelected = false; this.lineWidth = undefined; this.lineStyle = undefined; }
  attached({ requestUpdate, chart, series }) { this._requestUpdate = requestUpdate; this._chart = chart; this._series = series; }
  detached() { this._requestUpdate = null; this._chart = null; this._series = null; }
  requestUpdate() { this._requestUpdate?.(); }
  hitTest() { return Infinity; }
  isNear(x, y) { return this.hitTest(x, y) <= HIT_TOLERANCE; }
  hitTestHandle() { return -1; }
  moveBy() {}
  moveHandle() {}
  _ts() { return this._chart?.timeScale(); }
  _toXY(logical, price) {
    return { x: this._ts()?.logicalToCoordinate(logical), y: this._series?.priceToCoordinate(price) };
  }
}

// ═══════════════════════════════════════════════════════════════════
// GANN BOX (2 points — opposite corners)
// Draws horizontal Fib levels + vertical time divisions + diagonal
// ═══════════════════════════════════════════════════════════════════

const GANN_LEVELS = [0, 0.25, 0.382, 0.5, 0.618, 0.75, 1];
const GANN_COLORS = ['#808080', '#f59e0b', '#f97316', '#ef4444', '#f97316', '#f59e0b', '#808080'];

class GannBoxPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();
      const x1 = Math.min(p1.x, p2.x) * hpr, x2 = Math.max(p1.x, p2.x) * hpr;
      const y1 = Math.min(p1.y, p2.y) * vpr, y2 = Math.max(p1.y, p2.y) * vpr;
      const w = x2 - x1, h = y2 - y1;
      if (w < 2 || h < 2) { ctx.restore(); return; }

      // Outer box
      const _lw = s.lineWidth || 1;
      ctx.strokeStyle = hexToRgba(s.color, s.isHovered || s.isSelected ? 0.8 : 0.5);
      ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr;
      ctx.strokeRect(x1, y1, w, h);

      // Horizontal levels
      for (let i = 0; i < GANN_LEVELS.length; i++) {
        const lv = GANN_LEVELS[i];
        const yy = y1 + h * lv;
        ctx.strokeStyle = hexToRgba(GANN_COLORS[i], s.isHovered || s.isSelected ? 0.7 : 0.4);
        ctx.lineWidth = (lv === 0 || lv === 0.5 || lv === 1 ? 1.2 : 0.8) * vpr;
        ctx.setLineDash(lv === 0.5 ? [] : [3 * hpr, 2 * hpr]);
        ctx.beginPath(); ctx.moveTo(x1, yy); ctx.lineTo(x2, yy); ctx.stroke();
        // Label
        ctx.setLineDash([]);
        ctx.font = `${9 * vpr}px -apple-system,sans-serif`;
        ctx.fillStyle = hexToRgba(GANN_COLORS[i], 0.7);
        ctx.textBaseline = 'middle';
        ctx.fillText(`${(lv * 100).toFixed(1)}%`, x2 + 4 * hpr, yy);
      }

      // Vertical time divisions (same levels)
      for (let i = 0; i < GANN_LEVELS.length; i++) {
        const lv = GANN_LEVELS[i];
        const xx = x1 + w * lv;
        ctx.strokeStyle = hexToRgba(GANN_COLORS[i], 0.25);
        ctx.lineWidth = 0.8 * hpr;
        ctx.setLineDash([3 * hpr, 2 * hpr]);
        ctx.beginPath(); ctx.moveTo(xx, y1); ctx.lineTo(xx, y2); ctx.stroke();
      }

      // Diagonal from TL to BR
      ctx.setLineDash([]);
      ctx.strokeStyle = hexToRgba(s.color, 0.5);
      ctx.lineWidth = 1 * vpr;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      // Anti-diagonal
      ctx.beginPath(); ctx.moveTo(x1, y2); ctx.lineTo(x2, y1); ctx.stroke();

      // Zone fills
      for (let i = 0; i < GANN_LEVELS.length - 1; i++) {
        const ya = y1 + h * GANN_LEVELS[i], yb = y1 + h * GANN_LEVELS[i + 1];
        ctx.fillStyle = hexToRgba(GANN_COLORS[i], 0.04);
        ctx.fillRect(x1, ya, w, yb - ya);
      }

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class GannBoxPrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#f59e0b';
    this._p1 = null; this._p2 = null; this._view = new GannBoxPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (this.pt2) this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const minX = Math.min(this._p1.x, this._p2.x), maxX = Math.max(this._p1.x, this._p2.x);
    const minY = Math.min(this._p1.y, this._p2.y), maxY = Math.max(this._p1.y, this._p2.y);
    return (x >= minX && x <= maxX && y >= minY && y <= maxY) ? 0 : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// GANN FAN (2 points — origin + direction)
// Draws multiple angle lines: 1x1, 1x2, 2x1, 1x3, 3x1, 1x4, 4x1, 1x8, 8x1
// ═══════════════════════════════════════════════════════════════════

const GANN_FAN_ANGLES = [
  { ratio: 8, label: '8x1', color: '#ef4444' },
  { ratio: 4, label: '4x1', color: '#f97316' },
  { ratio: 3, label: '3x1', color: '#f59e0b' },
  { ratio: 2, label: '2x1', color: '#eab308' },
  { ratio: 1, label: '1x1', color: '#22c55e' },
  { ratio: 1/2, label: '1x2', color: '#14b8a6' },
  { ratio: 1/3, label: '1x3', color: '#06b6d4' },
  { ratio: 1/4, label: '1x4', color: '#3b82f6' },
  { ratio: 1/8, label: '1x8', color: '#8b5cf6' },
];

class GannFanPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();
      const x1 = p1.x * hpr, y1 = p1.y * vpr;
      const x2 = p2.x * hpr, y2 = p2.y * vpr;
      const dx = x2 - x1, dy = y2 - y1;
      const baseDist = Math.hypot(dx, dy);
      if (baseDist < 2) { ctx.restore(); return; }

      // The 1x1 line goes from p1 to p2. Other lines scale the angle.
      const extend = bs.width * 3;

      for (const angle of GANN_FAN_ANGLES) {
        // Scale: ratio * unit_price per unit_time
        // 1x1 = 45 degrees (base), 2x1 = steeper, 1x2 = flatter
        const scaledDy = dy * angle.ratio;
        const len = Math.hypot(dx, scaledDy);
        if (len < 1) continue;
        const sc = extend / len;
        const ex = x1 + dx * sc;
        const ey = y1 + scaledDy * sc;

        ctx.strokeStyle = hexToRgba(angle.color, angle.ratio === 1 ? 0.8 : 0.5);
        ctx.lineWidth = (angle.ratio === 1 ? 1.5 : 1) * vpr;
        ctx.setLineDash(angle.ratio === 1 ? [] : [4 * hpr, 3 * hpr]);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(ex, ey); ctx.stroke();

        // Label near the end or at chart edge
        const labelDist = Math.min(extend * 0.3, 300 * hpr);
        const labelSc = labelDist / len;
        const lx = x1 + dx * labelSc;
        const ly = y1 + scaledDy * labelSc;
        ctx.setLineDash([]);
        ctx.font = `${9 * vpr}px -apple-system,sans-serif`;
        ctx.fillStyle = hexToRgba(angle.color, 0.7);
        ctx.textBaseline = 'bottom';
        ctx.fillText(angle.label, lx + 3 * hpr, ly - 2 * vpr);
      }

      // Hover glow on main 1x1 line
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.25);
        ctx.lineWidth = 6 * hpr;
        ctx.setLineDash([]);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      }

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class GannFanPrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#22c55e';
    this._p1 = null; this._p2 = null; this._view = new GannFanPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (this.pt2) this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    return distToSeg(x, y, this._p1.x, this._p1.y, this._p2.x, this._p2.y);
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// GANN SQUARE (2 points — maps price/time into a square grid)
// ═══════════════════════════════════════════════════════════════════

class GannSquarePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();
      const x1 = Math.min(p1.x, p2.x) * hpr, x2 = Math.max(p1.x, p2.x) * hpr;
      const y1 = Math.min(p1.y, p2.y) * vpr, y2 = Math.max(p1.y, p2.y) * vpr;
      const w = x2 - x1, h = y2 - y1;
      if (w < 4 || h < 4) { ctx.restore(); return; }

      // Make it square (use max dimension)
      const size = Math.max(w, h);
      const sx = x1, sy = y1;

      // Grid lines (8 divisions)
      const divisions = 8;
      const step = size / divisions;
      ctx.strokeStyle = hexToRgba(s.color, 0.3);
      ctx.lineWidth = 0.8 * Math.min(hpr, vpr);
      ctx.setLineDash([2 * hpr, 2 * hpr]);

      for (let i = 0; i <= divisions; i++) {
        const off = i * step;
        // Horizontal
        ctx.beginPath(); ctx.moveTo(sx, sy + off); ctx.lineTo(sx + size, sy + off); ctx.stroke();
        // Vertical
        ctx.beginPath(); ctx.moveTo(sx + off, sy); ctx.lineTo(sx + off, sy + size); ctx.stroke();
      }

      // Diagonals
      ctx.setLineDash([]);
      ctx.strokeStyle = hexToRgba(s.color, 0.6);
      ctx.lineWidth = 1.2 * vpr;
      ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(sx + size, sy + size); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(sx + size, sy); ctx.lineTo(sx, sy + size); ctx.stroke();

      // Outer border
      ctx.strokeStyle = hexToRgba(s.color, 0.7);
      ctx.lineWidth = 1.5 * vpr;
      ctx.strokeRect(sx, sy, size, size);

      // Circle inscribed
      ctx.strokeStyle = hexToRgba(s.color, 0.2);
      ctx.lineWidth = 1 * vpr;
      ctx.beginPath();
      ctx.arc(sx + size / 2, sy + size / 2, size / 2, 0, Math.PI * 2);
      ctx.stroke();

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class GannSquarePrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#f59e0b';
    this._p1 = null; this._p2 = null; this._view = new GannSquarePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (this.pt2) this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const minX = Math.min(this._p1.x, this._p2.x), maxX = Math.max(this._p1.x, this._p2.x);
    const minY = Math.min(this._p1.y, this._p2.y), maxY = Math.max(this._p1.y, this._p2.y);
    return (x >= minX && x <= maxX && y >= minY && y <= maxY) ? 0 : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// REGRESSION TREND (2 points — linear regression channel)
// ═══════════════════════════════════════════════════════════════════

class RegressionPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const reg = s._regression;
      if (!reg) return;
      ctx.save();
      const { x1, y1, x2, y2, upperY1, upperY2, lowerY1, lowerY2 } = reg;

      // Channel fill
      ctx.fillStyle = hexToRgba(s.color, 0.06);
      ctx.beginPath();
      ctx.moveTo(x1, upperY1); ctx.lineTo(x2, upperY2);
      ctx.lineTo(x2, lowerY2); ctx.lineTo(x1, lowerY1);
      ctx.closePath(); ctx.fill();

      // Hover glow
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.25);
        ctx.lineWidth = 6 * vpr;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      }

      // Center regression line
      { const _lw = s.lineWidth || 1.5; ctx.strokeStyle = s.color;
      ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); }

      // Upper/lower bands
      ctx.strokeStyle = hexToRgba(s.color, 0.5);
      ctx.lineWidth = 1 * vpr;
      ctx.setLineDash([4 * hpr, 3 * hpr]);
      ctx.beginPath(); ctx.moveTo(x1, upperY1); ctx.lineTo(x2, upperY2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x1, lowerY1); ctx.lineTo(x2, lowerY2); ctx.stroke();
      ctx.setLineDash([]);

      // Label
      ctx.font = `bold ${9 * vpr}px -apple-system,sans-serif`;
      ctx.fillStyle = hexToRgba(s.color, 0.7);
      ctx.textBaseline = 'bottom';
      ctx.fillText('REGRESSION', x1 + 5 * hpr, Math.min(y1, y2) - 5 * vpr);

      if (s.isSelected) {
        drawHandle(ctx, s._p1.x, s._p1.y, hpr, vpr);
        drawHandle(ctx, s._p2.x, s._p2.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class RegressionTrendPrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#3b82f6';
    this._candles = options.candles || null;
    this._p1 = null; this._p2 = null; this._regression = null;
    this._view = new RegressionPV(this);
  }
  setCandles(candles) { this._candles = candles; }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (!this.pt2) { this._regression = null; return; }
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
    if (!this._p1 || !this._p2 || this._p1.x == null || this._p2.x == null) { this._regression = null; return; }

    // Compute linear regression from candle close prices in range
    const startL = Math.min(this.pt1.logical, this.pt2.logical);
    const endL = Math.max(this.pt1.logical, this.pt2.logical);
    const ts = this._ts();
    const candles = this._candles || [];
    const startIdx = Math.max(0, Math.round(startL));
    const endIdx = Math.min(candles.length - 1, Math.round(endL));

    if (endIdx - startIdx < 1) {
      // Fallback: just draw straight line between points
      const hpr = 1, vpr = 1;
      this._regression = {
        x1: this._p1.x * hpr, y1: this._p1.y * vpr,
        x2: this._p2.x * hpr, y2: this._p2.y * vpr,
        upperY1: (this._p1.y - 20) * vpr, upperY2: (this._p2.y - 20) * vpr,
        lowerY1: (this._p1.y + 20) * vpr, lowerY2: (this._p2.y + 20) * vpr,
      };
      return;
    }

    // Least squares
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, n = 0;
    for (let i = startIdx; i <= endIdx; i++) {
      if (!candles[i]) continue;
      const x = i - startIdx;
      const y = candles[i].close;
      sumX += x; sumY += y; sumXY += x * y; sumX2 += x * x; n++;
    }
    if (n < 2) { this._regression = null; return; }
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Standard deviation for channel width
    let sumResidual2 = 0;
    for (let i = startIdx; i <= endIdx; i++) {
      if (!candles[i]) continue;
      const x = i - startIdx;
      const predicted = slope * x + intercept;
      const residual = candles[i].close - predicted;
      sumResidual2 += residual * residual;
    }
    const stdDev = Math.sqrt(sumResidual2 / n) * 2;

    const regPriceStart = intercept;
    const regPriceEnd = slope * (endIdx - startIdx) + intercept;
    const regY1 = this._series.priceToCoordinate(regPriceStart);
    const regY2 = this._series.priceToCoordinate(regPriceEnd);
    const upperY1 = this._series.priceToCoordinate(regPriceStart + stdDev);
    const upperY2 = this._series.priceToCoordinate(regPriceEnd + stdDev);
    const lowerY1 = this._series.priceToCoordinate(regPriceStart - stdDev);
    const lowerY2 = this._series.priceToCoordinate(regPriceEnd - stdDev);
    const rx1 = ts.logicalToCoordinate(startIdx);
    const rx2 = ts.logicalToCoordinate(endIdx);

    if (rx1 == null || rx2 == null) { this._regression = null; return; }

    this._regression = {
      x1: rx1, y1: regY1, x2: rx2, y2: regY2,
      upperY1, upperY2, lowerY1, lowerY2,
    };
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    // Hit test against the visible regression channel
    const reg = this._regression;
    if (reg) {
      const dCenter = distToSeg(x, y, reg.x1, reg.y1, reg.x2, reg.y2);
      if (dCenter <= HIT_TOLERANCE) return dCenter;
      const dUpper = distToSeg(x, y, reg.x1, reg.upperY1, reg.x2, reg.upperY2);
      if (dUpper <= HIT_TOLERANCE) return dUpper;
      const dLower = distToSeg(x, y, reg.x1, reg.lowerY1, reg.x2, reg.lowerY2);
      if (dLower <= HIT_TOLERANCE) return dLower;
      // Inside channel
      const minX = Math.min(reg.x1, reg.x2), maxX = Math.max(reg.x1, reg.x2);
      const minY = Math.min(reg.upperY1, reg.upperY2, reg.lowerY1, reg.lowerY2);
      const maxY = Math.max(reg.upperY1, reg.upperY2, reg.lowerY1, reg.lowerY2);
      if (x >= minX && x <= maxX && y >= minY && y <= maxY) return 0;
    }
    return distToSeg(x, y, this._p1.x, this._p1.y, this._p2.x, this._p2.y);
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// DISJOINT CHANNEL (4 points — two independent parallel lines)
// ═══════════════════════════════════════════════════════════════════

class DisjointChannelPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const pts = [s._p1, s._p2, s._p3, s._p4].filter(p => p && p.x != null);
      if (pts.length < 2) return;
      ctx.save();

      const x1 = pts[0].x * hpr, y1 = pts[0].y * vpr;
      const x2 = pts[1].x * hpr, y2 = pts[1].y * vpr;

      // Line 1
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.25); ctx.lineWidth = 6 * vpr;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      }
      { const _lw = s.lineWidth || 1.5; ctx.strokeStyle = s.color; ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr; ctx.lineCap = 'round'; }
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();

      // Line 2
      if (pts.length >= 4) {
        const x3 = pts[2].x * hpr, y3 = pts[2].y * vpr;
        const x4 = pts[3].x * hpr, y4 = pts[3].y * vpr;
        ctx.beginPath(); ctx.moveTo(x3, y3); ctx.lineTo(x4, y4); ctx.stroke();

        // Fill between
        ctx.fillStyle = hexToRgba(s.color, 0.06);
        ctx.beginPath();
        ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
        ctx.lineTo(x4, y4); ctx.lineTo(x3, y3);
        ctx.closePath(); ctx.fill();
      } else if (pts.length === 3) {
        const x3 = pts[2].x * hpr, y3 = pts[2].y * vpr;
        ctx.setLineDash([4 * hpr, 3 * hpr]);
        ctx.beginPath(); ctx.moveTo(x3, y3); ctx.lineTo(x3 + (x2 - x1), y3 + (y2 - y1)); ctx.stroke();
        ctx.setLineDash([]);
      }

      if (s.isSelected) pts.forEach(p => drawHandle(ctx, p.x, p.y, hpr, vpr));
      ctx.restore();
    })}};
  }
}

export class DisjointChannelPrimitive extends Base {
  constructor(pt1, pt2, pt3, pt4, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.pt3 = pt3; this.pt4 = pt4;
    this.color = options.color || '#3b82f6';
    this._p1 = null; this._p2 = null; this._p3 = null; this._p4 = null;
    this._view = new DisjointChannelPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (this.pt2) this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
    if (this.pt3) this._p3 = this._toXY(this.pt3.logical, this.pt3.price);
    if (this.pt4) this._p4 = this._toXY(this.pt4.logical, this.pt4.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    let d = distToSeg(x, y, this._p1.x, this._p1.y, this._p2.x, this._p2.y);
    if (this._p3 && this._p4) d = Math.min(d, distToSeg(x, y, this._p3.x, this._p3.y, this._p4.x, this._p4.y));
    return d;
  }
  hitTestHandle(x, y) {
    const pts = [this._p1, this._p2, this._p3, this._p4];
    for (let i = 0; i < pts.length; i++) {
      if (pts[i] && Math.hypot(x - pts[i].x, y - pts[i].y) < HANDLE_HIT) return i;
    }
    return -1;
  }
  moveBy(dL, dP) {
    [this.pt1, this.pt2, this.pt3, this.pt4].filter(Boolean).forEach(p => { p.logical += dL; p.price += dP; });
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) this.pt1 = { logical: l, price: p };
    else if (i === 1) this.pt2 = { logical: l, price: p };
    else if (i === 2) this.pt3 = { logical: l, price: p };
    else this.pt4 = { logical: l, price: p };
    this._requestUpdate?.();
  }
  setPoint(n, pt) {
    if (n === 2) this.pt2 = pt;
    if (n === 3) this.pt3 = pt;
    if (n === 4) this.pt4 = pt;
    this._requestUpdate?.();
  }
  setPoint3(pt) { this.pt3 = pt; this._requestUpdate?.(); }
  setPoint4(pt) { this.pt4 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// FLAT TOP/BOTTOM CHANNEL (3 points — horizontal top/bottom + angled other side)
// ═══════════════════════════════════════════════════════════════════

class FlatChannelPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2, p3 = s._p3;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();
      const x1 = p1.x * hpr, y1 = p1.y * vpr;
      const x2 = p2.x * hpr, y2 = p2.y * vpr;

      // Angled line
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.25); ctx.lineWidth = 6 * vpr;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      }
      { const _lw = s.lineWidth || 1.5; ctx.strokeStyle = s.color; ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr; ctx.lineCap = 'round'; }
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();

      // Flat line (horizontal at p3's price level)
      if (p3 && p3.y != null) {
        const fy = p3.y * vpr;
        ctx.beginPath(); ctx.moveTo(x1, fy); ctx.lineTo(x2, fy); ctx.stroke();
        // Fill
        ctx.fillStyle = hexToRgba(s.color, 0.06);
        ctx.beginPath();
        ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
        ctx.lineTo(x2, fy); ctx.lineTo(x1, fy);
        ctx.closePath(); ctx.fill();
        // Median
        ctx.strokeStyle = hexToRgba(s.color, 0.3); ctx.lineWidth = 1 * vpr;
        ctx.setLineDash([3, 3].map(d => d * hpr));
        ctx.beginPath(); ctx.moveTo(x1, (y1 + fy) / 2); ctx.lineTo(x2, (y2 + fy) / 2); ctx.stroke();
        ctx.setLineDash([]);
      }

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
        if (p3) drawHandle(ctx, p3.x ?? p1.x, p3.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class FlatChannelPrimitive extends Base {
  constructor(pt1, pt2, pt3, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.pt3 = pt3;
    this.color = options.color || '#3b82f6';
    this._p1 = null; this._p2 = null; this._p3 = null;
    this._view = new FlatChannelPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
    if (this.pt3) this._p3 = this._toXY(this.pt3.logical || this.pt1.logical, this.pt3.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    let d = distToSeg(x, y, this._p1.x, this._p1.y, this._p2.x, this._p2.y);
    if (this._p3) {
      const fy = this._p3.y;
      d = Math.min(d, distToSeg(x, y, this._p1.x, fy, this._p2.x, fy));
    }
    return d;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    if (this._p3 && Math.hypot(x - (this._p3.x ?? this._p1?.x), y - this._p3.y) < HANDLE_HIT) return 2;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    if (this.pt3) this.pt3 = { logical: (this.pt3.logical || this.pt1.logical) + dL, price: this.pt3.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) this.pt1 = { logical: l, price: p };
    else if (i === 1) this.pt2 = { logical: l, price: p };
    else this.pt3 = { logical: l, price: p };
    this._requestUpdate?.();
  }
  setPoint3(pt) { this.pt3 = pt; this._requestUpdate?.(); }
}
